import { Box, Flex, Tabs, Button, Indicator } from '@mantine/core';
import { IconChevronLeft, IconMessage, IconPointFilled } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import theme from 'theme';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import { Link, useNavigate } from 'react-router-dom';
import { DASHBOARD_PATH, HELPDESK_DETAIL_PAGE_PATH, TICKET_PAGE_PATH } from 'routes/paths';
import P2Medium from 'theme/components/Typography/P2Medium';
import HelpDeskItem from 'components/HelpDeskItem';
import { useApi } from 'api/api-context';
import panic from 'panic';
import { HelpdeskGetJobsResponse } from 'api/actions/helpdesk-get-jobs/helpdesk-get-jobs-response';

/** */
const HelpDesk: React.FC = () => {
  const { _t } = useLang();
  const { getAction } = useApi();
  const [jobs, setJobs] = useState<NonNullable<HelpdeskGetJobsResponse['jobs']>>([]);
  const navigate = useNavigate();

  /** */
  const loadJobs = () => {
    const getJobs = getAction('HelpdeskGetJobs');
    getJobs()
      .then((response) => {
        const jobs = response.data || [];
        setJobs(jobs as any);
      })
      .catch(panic);
  };

  useEffect(() => {
    loadJobs();
  }, [getAction]);

  return (
    <>
      <SimpleLayout>
        <Box p="1rem">
          <Flex direction="row" align="center">
            <Link
              to={DASHBOARD_PATH.original}
              style={{ textDecoration: 'none', display: 'flex', alignContent: 'center', marginBottom: '0.5rem' }}
            >
              <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
              <P2Medium color={theme.colors?.gray?.[9]}>{_t('Back')}</P2Medium>
            </Link>
          </Flex>
          <Flex gap="0.5rem">
            <IconMessage size="2rem" color={theme.colors?.gray?.[12]} />
            <H1SemiBold color={theme.colors?.gray?.[12]}>{_t('Helpdesk')}</H1SemiBold>
          </Flex>
        </Box>

        <Box>
          <Tabs defaultValue="gallery" variant="pills">
            <Tabs.List px="1rem" grow>
              <Tabs.Tab rightSection={<Indicator pl="0.3125rem" color="#FF364E" />} value="gallery">
                {_t('Disorders')}
              </Tabs.Tab>
              <Tabs.Tab value="messages">{_t('Notifications')}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="gallery" p="1rem">
              {jobs
                .filter((job) => job.type === 4)
                .sort((a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime())
                .map((job) => (
                  <Link
                    key={job.id}
                    to={HELPDESK_DETAIL_PAGE_PATH.insert({ id: job.id })}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <HelpDeskItem
                      title={job.name ?? ''}
                      size={new Date(job.createdAt ?? '').toLocaleString('sk-SK')}
                      badges={[
                        { variant: getStatusVariant(job.status ?? ''), text: _t(formatStatus(job.status ?? '')) },
                        { variant: 'white', text: job.targetObjectName ?? '' },
                      ]}
                    />
                  </Link>
                ))}
            </Tabs.Panel>

            <Tabs.Panel value="messages" p="1rem">
              {jobs
                .filter((job) => job.type !== 4)
                .sort((a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime())
                .map((job) => (
                  <Link
                    key={job.id}
                    to={HELPDESK_DETAIL_PAGE_PATH.insert({ id: job.id })}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <HelpDeskItem
                      title={job.name ?? ''}
                      size={new Date(job.createdAt ?? '').toLocaleString('sk-SK')}
                      badges={[
                        { variant: getStatusVariant(job.status ?? ''), text: _t(formatStatus(job.status ?? '')) },
                        { variant: 'white', text: job.targetObjectName ?? '' },
                      ]}
                    />
                  </Link>
                ))}
            </Tabs.Panel>
          </Tabs>
        </Box>

        <Button
          leftSection={<IconPointFilled style={{ color: '#19CD6C' }} />}
          variant="primary"
          style={{
            position: 'fixed',
            right: '1rem',
            bottom: '2.5rem',
            boxShadow: '0px 6px 12px 0px rgba(85, 31, 255, 0.30)',
          }}
          size="lg"
          onClick={() => {
            navigate(TICKET_PAGE_PATH.original);
          }}
        >
          {_t('I need help')}
        </Button>
      </SimpleLayout>
    </>
  );
};

/**
 * Get status variant for badge
 */
const getStatusVariant = (status: string | number): string => {
  switch (status.toString()) {
    case '0': // Unknown
      return 'gray';
    case '1': // Zadaná
      return 'blue';
    case '2': // Rozpracovaná
      return 'yellow';
    case '3': // Zrealizovaná
      return 'orange';
    case '4': // Hotová
      return 'green';
    case '5': // Zrušená
      return 'gray';
    default: // Neznáma / chyba
      return 'red';
  }
};

/**
 * Format status text
 */
const formatStatus = (status: string | number): string => {
  switch (status.toString()) {
    case '0':
      return 'Unknown';
    case '1':
      return 'Submitted';
    case '2':
      return 'In progress';
    case '3':
      return 'Implemented';
    case '4':
      return 'Done';
    case '5':
      return 'Canceled';
    default:
      return status.toString();
  }
};

export default HelpDesk;
